<template>
    <button v-if="!custom" v-bind="attrs" type="button" role="button" :aria-label="ariaLabel" @click="navigate">
        <slot :navigate="navigate" />
    </button>
    <template v-else>
        <slot :navigate="navigate" />
    </template>
</template>

<script setup lang="ts">
    import {computed, useAttrs, useSlots} from 'vue';
    import {useNuxtApp} from '#imports';
    import {safelyNavigateTo} from '@malt/utils';

    defineOptions({
        inheritAttrs: false,
    });

    type AuthLinkRole = 'signin' | 'signup' | 'signup-client' | 'signup-freelancer';

    const AUTH_PATH_BY_ROLE: Record<AuthLinkRole, string> = {
        signin: '/signin',
        signup: '/who-are-you',
        'signup-client': '/signup-client/email',
        'signup-freelancer': '/profile/freelancer-signup/your-freelancing-experience',
    };

    interface Props {
        role: AuthLinkRole;
        redirect?: string;
        pageSource?: string;
        componentSource?: string;
        custom?: boolean;
    }

    const {role, redirect, pageSource, componentSource, custom} = defineProps<Props>();

    defineSlots<{
        default(slotProps: {navigate: () => Promise<unknown>}): unknown;
    }>();

    const attrs = useAttrs();

    const slots = useSlots();

    const {$withLocalePrefix} = useNuxtApp() as unknown as {
        $withLocalePrefix?: (path: string) => string;
    };

    const ariaLabel = computed(() => {
        if (custom) {
            return '';
        }

        return (
            slots
                .default?.()
                ?.map((v) => v.children)
                .join('') ?? ''
        );
    });

    async function navigate() {
        const path = AUTH_PATH_BY_ROLE[role];
        const params = new URLSearchParams();

        if (componentSource) {
            params.append('componentSource', componentSource);
        }
        if (pageSource) {
            params.append('pageSource', pageSource);
        }
        if (redirect) {
            params.append('redirect', redirect);
        }

        const queryString = params.toString();
        const localizedPath = $withLocalePrefix?.(path) ?? path;
        const url = queryString ? `${localizedPath}?${queryString}` : localizedPath;

        return safelyNavigateTo(url, {externalToThisApp: true});
    }
</script>
